// The skinned meshes of a rig, gathered so something else can bind to them.
//
// Two features attach geometry that is NOT in the rig GLB and needs to ride it
// anyway: hair strands published as a rigid node under the head joint
// (hairStrands.js) and a Marvelous garment with no skin at all
// (garment/garmentAttach.js). Both answer the same question first — which
// skeletons does this rig have, where are their skinned vertices in world
// space at the CURRENT pose, and what are each vertex's joint influences — so
// they ask it in one place.
//
// The positions are read through the position attribute and pushed through
// `bindMatrix` — SKIN SPACE, not world — and the influences through the
// skinIndex/skinWeight attributes' accessors, so a quantised WEIGHTS_0
// (Unreal's exporter writes normalised 8-bit) is decoded rather than read raw.
// One skin set or three, whatever the geometry carries (skinInfluences.js).
//
// SKIN SPACE IS NOT WORLD SPACE, and reading `matrixWorld` here was wrong on
// exactly the rigs where the two differ. three skins a vertex as
// `matrixWorld x bindMatrixInverse x boneMatrix x bindMatrix x p`, and for a
// glTF character GLTFLoader binds with the IDENTITY (GLTFLoader.js: `mesh.bind(
// skeleton, _identityMatrix )`), so the space the skeleton actually deforms is
// the RAW GEOMETRY — the mesh node's own transform is cancelled again by the
// per-frame `bindMatrixInverse` of the default attached bind mode. Measured
// (2026-09-17): aoi, danielle and isaac carry their mesh nodes at the identity,
// so world and skin space coincide there and nothing notices; myra's rig hangs
// every mesh under an `Armature` node scaled 0.01 while her geometry is already
// in metres, so `matrixWorld` reported a 1.6 CENTIMETRE body — and a garment
// 1.47 m tall then bound to nothing at all (100% unbound) because no vertex of
// hers was within the 15 cm threshold of it.
//
// This gather is only meaningful at the BIND POSE — every consumer projects
// onto the skin as it stands right now, so a rig read mid-animation binds to
// the wrong surface. Callers that can be reached after playback starts should
// measure the departure from bind and say so; see hairStrands.js's
// restBindDeviation for the shape of that check.

import * as THREE from 'three'
import { VertexGrid } from './skinBind.js'
import { skinIndexAttr, skinWeightAttr, MAX_SKIN_SETS } from './skinInfluences.js'

const _v = new THREE.Vector3()

/** Element-wise tolerance for calling two bind poses the same rig. */
export const SKELETON_MATCH_EPS = 1e-5

/**
 * Are these two skeletons the SAME rig, laid out in the same order — so that a
 * bone index means the same joint in both?
 *
 * Decided on the bind matrices, NOT on names. An Unreal export ships one
 * skeleton copy per skin, and GLTFLoader uniquifies the duplicate node names —
 * `root`, `root_2`, `root_3` — so a name comparison needs to strip a trailing
 * `_N`, and MetaHuman bones are genuinely called `spine_01`, `neck_01`,
 * `middle_02_l`. Stripping turns `spine_01` into `spine` and makes two
 * identical rigs look different, which is exactly what it did on
 * aoi-fridaytest. `boneInverses` cannot be fooled that way: measured there,
 * his body, tee, jeans and loafers agree to 3.6e-7 element-wise, while the
 * 875-bone hair rig differs in bone count outright.
 *
 * @param {THREE.Skeleton} a
 * @param {THREE.Skeleton} b
 * @param {number} [eps]
 */
export function skeletonsInterchangeable(a, b, eps = SKELETON_MATCH_EPS) {
  if (a === b) return true
  if (!a || !b || a.bones.length !== b.bones.length) return false
  if (a.boneInverses.length !== b.boneInverses.length) return false
  for (let i = 0; i < a.boneInverses.length; i++) {
    const ea = a.boneInverses[i].elements, eb = b.boneInverses[i].elements
    for (let k = 0; k < 16; k++) if (Math.abs(ea[k] - eb[k]) > eps) return false
  }
  return true
}

/**
 * Where a bone sits in SKIN SPACE at the bind pose.
 *
 * `boneInverses[i]` is the map from skin space into that bone's own frame, so
 * its inverse is the bone's placement. Read it from here rather than from
 * `bone.matrixWorld`: the latter is the bone NOW, in WORLD, and on a rig whose
 * meshes hang under a scaled node the two differ by that scale — which is how a
 * garment came to be measured against a body a hundred times too small.
 *
 * @param {THREE.Skeleton} skeleton
 * @param {number} i
 * @param {THREE.Matrix4} [out]
 */
export function boneSkinMatrix(skeleton, i, out = new THREE.Matrix4()) {
  return out.copy(skeleton.boneInverses[i]).invert()
}

/**
 * Skin space -> world, at the current pose.
 *
 * This is exactly the matrix three's skinning applies to a vertex that rides
 * one bone rigidly (`bone.matrixWorld * boneInverse`), so at the bind pose every
 * bone gives the same answer and bone 0 is as good as any. Its inverse is what
 * takes a world-space point into the space `collectSkinTargets` reports.
 *
 * @param {THREE.Skeleton} skeleton
 * @param {THREE.Matrix4} [out]
 */
export function skinToWorldMatrix(skeleton, out = new THREE.Matrix4()) {
  if (!skeleton?.bones?.length) return out.identity()
  skeleton.bones[0].updateWorldMatrix(true, false)
  return out.multiplyMatrices(skeleton.bones[0].matrixWorld, skeleton.boneInverses[0])
}

/**
 * Every skeleton under `root`, with its skinned vertices indexed for
 * nearest-point queries.
 *
 * @param {THREE.Object3D} root
 * @param {{ skip?: (o: THREE.Object3D) => boolean }} [opts]
 *   `skip` excludes objects that are themselves bound geometry — hair ribbons,
 *   an already-attached garment — so a second attach never binds to the first.
 * @returns {Array<{ skeleton: THREE.Skeleton, meshes: THREE.SkinnedMesh[],
 *   sets: Array<Array<{index: Float32Array, weight: Float32Array}>>,
 *   grid: VertexGrid, owner: Uint32Array, local: Uint32Array }>}
 *   `grid` indexes a concatenation of every mesh's vertices; `owner[v]` is the
 *   mesh it came from and `local[v]` its index within that mesh, which is what
 *   `topInfluences(sets[owner[v]], local[v], …)` needs.
 */
export function collectSkinTargets(root, { skip } = {}) {
  const bySkeleton = new Map()
  root.traverse((o) => {
    if (!o.isSkinnedMesh || !o.skeleton) return
    if (skip && skip(o)) return
    const a = o.geometry?.attributes
    if (!a?.position || !a.skinIndex || !a.skinWeight) return
    let t = bySkeleton.get(o.skeleton)
    if (!t) { t = { skeleton: o.skeleton, meshes: [] }; bySkeleton.set(o.skeleton, t) }
    t.meshes.push(o)
  })
  const targets = []
  for (const t of bySkeleton.values()) {
    let total = 0
    for (const m of t.meshes) total += m.geometry.attributes.position.count
    const skin = new Float32Array(total * 3)
    const owner = new Uint32Array(total), local = new Uint32Array(total)
    let k = 0
    t.sets = t.meshes.map((m, mi) => {
      const g = m.geometry, p = g.attributes.position
      for (let i = 0; i < p.count; i++, k++) {
        _v.fromBufferAttribute(p, i).applyMatrix4(m.bindMatrix)
        skin[k * 3] = _v.x; skin[k * 3 + 1] = _v.y; skin[k * 3 + 2] = _v.z
        owner[k] = mi; local[k] = i
      }
      const sets = []
      for (let n = 0; n < MAX_SKIN_SETS; n++) {
        const ji = g.attributes[skinIndexAttr(n)], wi = g.attributes[skinWeightAttr(n)]
        if (!ji || !wi) break
        const index = new Float32Array(p.count * 4), weight = new Float32Array(p.count * 4)
        for (let i = 0; i < p.count; i++) for (let c = 0; c < 4; c++) {
          index[i * 4 + c] = ji.getComponent(i, c)
          weight[i * 4 + c] = wi.getComponent(i, c)
        }
        sets.push({ index, weight })
      }
      return sets
    })
    t.grid = new VertexGrid(skin)
    t.skinToWorld = skinToWorldMatrix(t.skeleton)
    t.owner = owner
    t.local = local
    targets.push(t)
  }
  return targets
}

/**
 * Fuse targets whose skeletons are interchangeable into one weight source.
 *
 * A published MetaHuman does not have "a body skin": the body mesh's
 * under-clothing surface is CULLED, so on aoi-fridaytest `Body` is only the
 * arms, neck, hands and feet and covers 43% of a t-shirt and joggers. His
 * torso and legs exist only as his own tee and jeans meshes, each on its own
 * copy of the skeleton. No single skin is a good weight source, and picking
 * one orphaned a quarter of the garment whichever was chosen.
 *
 * Because the copies are the SAME rig in the same order (verified by
 * `skeletonsInterchangeable`, not assumed), a bone index from any of them is
 * valid in all of them — so their vertices can be poured into ONE grid and
 * their influences used unchanged. The merged target then covers the whole
 * character's surface, clothed and bare alike.
 *
 * Incompatible skins (a face rig with a different bone count) stay separate.
 *
 * @param {ReturnType<typeof collectSkinTargets>} targets
 * @param {number} [eps]
 * @returns {ReturnType<typeof collectSkinTargets>} one entry per compatible group
 */
export function mergeCompatibleSkins(targets, eps = SKELETON_MATCH_EPS) {
  const groups = []
  for (const t of targets) {
    const g = groups.find((x) => skeletonsInterchangeable(x[0].skeleton, t.skeleton, eps))
    if (g) g.push(t); else groups.push([t])
  }
  return groups.map((group) => {
    if (group.length === 1) return group[0]
    const total = group.reduce((n, t) => n + t.grid.count, 0)
    const world = new Float32Array(total * 3)
    const owner = new Uint32Array(total), local = new Uint32Array(total)
    const meshes = [], sets = []
    let k = 0
    for (const t of group) {
      const meshBase = meshes.length
      meshes.push(...t.meshes)
      sets.push(...t.sets)
      for (let i = 0; i < t.grid.count; i++, k++) {
        world[k * 3] = t.grid.positions[i * 3]
        world[k * 3 + 1] = t.grid.positions[i * 3 + 1]
        world[k * 3 + 2] = t.grid.positions[i * 3 + 2]
        owner[k] = meshBase + t.owner[i]
        local[k] = t.local[i]
      }
    }
    return {
      // Every skeleton in the group is interchangeable, so which one carries
      // the garment is arbitrary; take the first for determinism.
      skeleton: group[0].skeleton,
      meshes, sets, owner, local,
      grid: new VertexGrid(world),
      mergedFrom: group.length,
    }
  })
}
